import {
  Alert,
  Box,
  Button,
  Modal,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { FullScreenAnimationLoader } from 'src/App/Components/Loaders';

import {
  COLOR_BACKGROUND_1,
  COLOR_GREY_400,
  COLOR_PRIMARY_2,
  COLOR_SUCCESS_1,
  COLOR_TEXT_2,
} from 'src/colors';
import { callApi } from 'src/core/callApi';

const AffiliatePartner = () => {
  const theme = useTheme();
  const { partnerKey: routePartnerKey } = useParams<{ partnerKey: string }>();
  const [partnerKey, setPartnerKey] = useState<string>('');
  const [openPartnerPasswordModal, setOpenPartnerPasswordModal] = useState<boolean>(false);
  const [affiliatePartnerPassword, setAffiliatePartnerPassword] = useState<any>();
  const [totalAggregatedData, setTotalAggregatedData] = useState<any>();
  const [lastMonthData, setLastMonthData] = useState<any>();
  const [allData, setAllData] = useState<any>();
  const [graphData, setGraphData] = useState<any>();
  const [aggregatedData, setAggregatedData] = useState<any>();
  const [showLoader, setShowLoader] = useState<boolean>(false);
  const [partnerName, setPartnerName] = useState<string>('');
  const [partnerEmail, setPartnerEmail] = useState<string>('');
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const isMobileScreen = useMediaQuery(theme.breakpoints.down(520));

  useEffect(() => {
    const value = localStorage.getItem('AffiliatePartnerPassword');
    setAffiliatePartnerPassword(value);
    if (!value) {
      setOpenPartnerPasswordModal(true);
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setShowLoader(true);
      setShowAlert(false);
      const URL =
        'https://ak27jez2w0.execute-api.us-east-2.amazonaws.com/default/affiliatePartnerStats';
      const config = {
        queryParams: {
          affiliatePartnerPassword: encodeURIComponent(affiliatePartnerPassword),
          affiliateKey: partnerKey,
          operationType: 'retrieve',
        },
      };
      const res = await callApi(URL, config, false);
      if (res?.data) {
        setTotalAggregatedData(res.data.total_stats);
        setLastMonthData(res.data.last_month_stats);
        setAllData(res.data.formatted_data);
        setPartnerName(res.data.name);
        setPartnerEmail(res.data.email);
        setShowLoader(false);
      }

      if (res?.error) {
        setShowLoader(false);
        setShowAlert(true);
        setErrorMessage(res.error)
      }
    };
    if (partnerKey && affiliatePartnerPassword) {
      fetchData();
    }
  }, [partnerKey, affiliatePartnerPassword]);

  useEffect(() => {
    if (totalAggregatedData && lastMonthData) {
      let aggregateData = [
        {
          title: 'Last Month Unique Visitors',
          value: lastMonthData.visitors_count,
        },
        {
          title: 'Last Month Sign Up Visitors',
          value: lastMonthData.signup_count,
        },
        {
          title: 'Last Month Subscribed Visitors',
          value: lastMonthData.subscription_count,
        },
        {
          title: 'Total Unique Visitors',
          value: totalAggregatedData.visitors_count,
        },
        {
          title: 'Total Sign Up Visitors',
          value: totalAggregatedData.signup_count,
        },
        {
          title: 'Total Subscribed Visitors',
          value: totalAggregatedData.subscription_count,
        },
      ];
      setAggregatedData(aggregateData);
    }

    if (allData) {
      let mapdata: any = [];
      for (let data of allData) {
        mapdata.push({
          date: data['date'],
          newVisitors: data['visitors_count'],
          signupVisitors: data['signup_count'],
          subscribedVisitors: data['subscription_count'],
        });
      }
      setGraphData(mapdata.reverse());
    }
  }, [totalAggregatedData, lastMonthData, allData]);

  useEffect(() => {
    setPartnerKey(routePartnerKey);
  }, [routePartnerKey]);

  const handleSaveAdminPassword = password => {
    setOpenPartnerPasswordModal(false);
    setAffiliatePartnerPassword(password);
    localStorage.setItem('AffiliatePartnerPassword', password);
  };

  const PartnerPasswordModal = () => {
    const [password, setPassword] = useState<string>('');
    return (
      <Modal open={openPartnerPasswordModal}>
        <Box
          sx={{
            width: '50vw',
            height: '120px',
            background: '#fff',
            position: 'absolute',
            top: '40%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            padding: '30px',
            borderRadius: 4,
          }}
        >
          <Typography variant="h5">Enter The Affiliate Partner Password:</Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '10px' }}>
            <TextField
              id="outlined-basic"
              variant="outlined"
              label="Password"
              value={password}
              onChange={e => {
                setPassword(e.target.value);
              }}
              sx={{ width: '100%' }}
            />
            <Button
              sx={{
                backgroundColor: COLOR_SUCCESS_1,
                color: 'white',
                ':hover': { backgroundColor: COLOR_SUCCESS_1 },
              }}
              onClick={() => {
                handleSaveAdminPassword(password);
              }}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Modal>
    );
  };

  const InfluencerKPI = ({ title, value }) => {
    return (
      <Box
        sx={{
          height: '100px',
          width: '150px',
          backgroundColor: COLOR_PRIMARY_2,
          borderRadius: '10px',
          display: 'flex',
          flexDirection: 'column',
          padding: '10px',
          margin: '10px',
        }}
      >
        <Typography
          variant="h5"
          sx={{
            color: '#ffff',
            height: '50px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
          }}
        >
          {value}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: '#ffff',
            height: '50px',
            // display: 'flex',
            // justifyContent: 'center',
            // alignItems: 'center',
            textAlign: 'center',
          }}
        >
          {title}
        </Typography>
      </Box>
    );
  };

  return (
    <>
      <PartnerPasswordModal />
      <Box
        sx={{
          backgroundColor: COLOR_BACKGROUND_1,
          padding: '10px',
          height: '97vh',
          overflowY: 'scroll',
        }}
      >
        {showLoader && !openPartnerPasswordModal && <FullScreenAnimationLoader />}

        {/* Reset Password Button */}
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6">Partner ID: {partnerKey}</Typography>
          </Box>
          <Button
            sx={{
              backgroundColor: COLOR_PRIMARY_2,
              color: 'white',
              '&:hover': { backgroundColor: COLOR_PRIMARY_2 },
            }}
            onClick={() => {
              setOpenPartnerPasswordModal(true);
            }}
          >
            Reset Password
          </Button>
        </Box>

        {/* Aggregated KPIs */}

        {aggregatedData && (
          <Box sx={{ width: '100%' }}>
            <Typography variant="h6">Name: {partnerName}</Typography>
            <Typography variant="h6">Email: {partnerEmail}</Typography>
          </Box>
        )}
        {aggregatedData && (
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              flexWrap: 'wrap',
              marginTop: '20px',
              justifyContent: 'center',
            }}
          >
            {aggregatedData.map((data, index) => (
              <InfluencerKPI key={index} title={data.title} value={data.value} />
            ))}
          </Box>
        )}

        {graphData && (
          <Box sx={{ width: '100%', marginTop: '10px' }}>
            <Typography variant="h5" sx={{ fontWeight: 500, textAlign: 'center' }}>
              Daily Level data
            </Typography>

            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <ResponsiveContainer height={350} width="80%">
                <LineChart
                  height={300}
                  data={graphData}
                  margin={{
                    top: 20,
                    right: isMobileScreen ? 0 : 30,
                    bottom: 20,
                    left: isMobileScreen ? 0 : 30,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip /> {/* Add Tooltip component here */}
                  <Line type="monotone" dataKey="newVisitors" stroke="#8884d8" />
                  <Line type="monotone" dataKey="signupVisitors" stroke="#82ca9d" />
                  <Line type="monotone" dataKey="subscribedVisitors" stroke="#ffc658" />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Box>
        )}

        {/* Alert */}
        {showAlert && (
          <Alert severity="error" sx={{ marginTop: '10px' }}>
            {errorMessage}
          </Alert>
        )}
      </Box>
    </>
  );
};

export default AffiliatePartner;
